import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send } from "lucide-react";

type ChatComposerProps = {
  draft: string;
  onDraftChange: (value: string) => void;
  onSend: () => void;
  disabled?: boolean;
  sending?: boolean;
  placeholder?: string;
};

export function ChatComposer({
  draft,
  onDraftChange,
  onSend,
  disabled = false,
  sending = false,
  placeholder = "Type a message…",
}: ChatComposerProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!disabled && !sending && draft.trim()) {
        onSend();
      }
    }
  };

  return (
    <div className="flex items-end gap-2 p-4 sm:p-5 border-t border-primary/20 bg-dark-card/95 backdrop-blur-sm flex-shrink-0">
      <div
        className="flex-1 flex items-end rounded-2xl border border-primary/15 bg-dark-slate/50 px-3 py-2 shadow-sm focus-within:border-primary/30 focus-within:ring-1 focus-within:ring-primary/20"
      >
        <Textarea
          ref={textareaRef}
          placeholder={placeholder}
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled || sending}
          rows={1}
          className="min-h-[44px] max-h-32 resize-none bg-transparent text-white border-0 focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-gray-light py-2.5"
        />
      </div>
      <Button
        type="button"
        onClick={onSend}
        disabled={disabled || sending || !draft.trim()}
        aria-label="Send message"
        className="rounded-full h-11 w-11 p-0 gradient-primary text-black flex items-center justify-center hover:opacity-90 disabled:opacity-50 flex-shrink-0"
      >
        <Send className="w-4 h-4" />
      </Button>
    </div>
  );
}
